import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Divider,
  Stack,
  Link,
} from '@mui/material';
import type {
  Customer,
  PackingSlipCreateInput,
  ShipMethods,
} from '@repo/api-contract';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  gridItemProps,
  textFieldProps,
  textFieldSelectProps,
} from '../../constants/form';
import dayjs, { Dayjs } from 'dayjs';
import { FormikProps } from 'formik';
import { getFormikField } from '../../../../util/fornik-helpers';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from '../../../../state';
import SelectSearchCustomer from '../../../utils/SelectSearchCustomer';
import {
  closeModal,
  openPackingSlipModal,
  PackingSlipModalMode,
} from '../../../../state/packingSlipModalSlice';
import { openSearchCustomerModal } from '../../../../state/customerSearchModalSlice';
import { openNewCustomerModal } from '../../../../state/customerModalSlice';

export default function PackingSlipDetails({
  formik,
  shipMethods,
  viewMode,
}: {
  formik: FormikProps<PackingSlipCreateInput>;
  shipMethods: ShipMethods[];
  viewMode: boolean;
}): JSX.Element {
  const { t: tGC } = useTranslation([], { keyPrefix: 'team.Global.Common' });
  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { t: tPS } = useTranslation([], {
    keyPrefix: 'team.Modals.Packing Slips.createAndEdit.Add Packing Slip',
  });
  const dispatch = useDispatch();

  const { mode } = useSelector(
    ({ packingSlipModalSlice }) => packingSlipModalSlice,
  );

  const packingSlipDate =
    formik.values.packingSlipDate !== 0
      ? dayjs(formik.values.packingSlipDate)
      : null;

  const handleChangePackingSlipDate = (newValue: Dayjs | null) => {
    const unixTime = newValue ? newValue.valueOf() : null;
    formik.setFieldValue('packingSlipDate', unixTime);
  };

  const shipDate =
    formik.values.shipDate !== 0 ? dayjs(formik.values.shipDate) : null;

  const handleChangeShipDate = (newValue: Dayjs | null) => {
    const unixTime = newValue ? newValue.valueOf() : null;
    formik.setFieldValue('shipDate', unixTime);
  };

  const selectedShipMethod = getFormikField(formik, 'shipMethod').value;

  const onImport = (selectedCustomers: Customer[]) => {
    const customer = selectedCustomers.at(0);
    if (!customer) return;

    formik.setFieldValue('customer.code', customer.code);
    formik.setFieldValue('customer.CustomerId', customer.CustomerId);
    formik.setFieldValue('customer.companyName', customer.details.companyName);
    dispatch(openPackingSlipModal());
  };

  const onCancel = () => {
    dispatch(openPackingSlipModal());
  };

  const openBrowseCustomerModal = () => {
    dispatch(closeModal());
    dispatch(
      openSearchCustomerModal({ onImport, onCancel, multiSelect: false }),
    );
  };

  const newCustomerCallBack = async (
    customer: Customer | undefined,
  ): Promise<void> => {
    dispatch(openPackingSlipModal());
    if (!customer) return;

    formik.setFieldValue('customer.code', customer.code);
    formik.setFieldValue('customer.CustomerId', customer.CustomerId);
    formik.setFieldValue('customer.companyName', customer.details.companyName);
  };

  const openCreateCustomerModal = () => {
    dispatch(closeModal());
    dispatch(openNewCustomerModal({ createCallback: newCustomerCallBack }));
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('code', event.target.value);
  };

  const handleChangeCustomer = (customer: Customer | null) => {
    if (customer != null) {
      formik.setFieldValue('customer.code', customer.code);
      formik.setFieldValue('customer.CustomerId', customer.CustomerId);
      formik.setFieldValue(
        'customer.companyName',
        customer.details.companyName,
      );
    } else {
      formik.setFieldValue('customer.code', '');
      formik.setFieldValue('customer.CustomerId', '');
      formik.setFieldValue('customer.companyName', '');
    }
  };

  const currentCustomer = formik.values.customer.companyName
    ? ({
        code: formik.values.customer.code,
        CustomerId: formik.values.customer.CustomerId,
        details: {
          companyName: formik.values.customer.companyName,
        },
      } as Customer)
    : null;

  return (
    <>
      <Typography color="primary">{tPS('Details')}</Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
        sx={{ marginY: 2 }}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode || mode === PackingSlipModalMode.EDIT}
              {...textFieldProps}
              label={tPS('Packing Slip No.')}
              name="code"
              error={Boolean(getFormikField(formik, 'code').error)}
              helperText={getFormikField(formik, 'code').error}
              value={getFormikField(formik, 'code').value}
              onChange={handleCodeChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tPS('Packing Slip Date')}
                {...textFieldProps}
                value={packingSlipDate}
                onChange={handleChangePackingSlipDate}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(
                      getFormikField(formik, 'packingSlipDate').error,
                    ),
                    helperText: getFormikField(formik, 'packingSlipDate').error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid {...gridItemProps}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={viewMode}
                label={tPS('Shipping Date')}
                {...textFieldProps}
                value={shipDate}
                onChange={handleChangeShipDate}
                views={['year', 'month', 'day']}
                slotProps={{
                  textField: {
                    error: Boolean(getFormikField(formik, 'shipDate').error),
                    helperText: getFormikField(formik, 'shipDate').error,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tPS('Ship Method')}
              select
              name="shipMethod"
              disabled={viewMode}
              error={Boolean(getFormikField(formik, 'shipMethod').error)}
              helperText={getFormikField(formik, 'shipMethod').error}
              value={selectedShipMethod}
              onChange={formik.handleChange}
            >
              {shipMethods.map((method) => (
                <MenuItem key={method.ShipMethodId} value={method.ShipMethodId}>
                  {method.method}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps}>
            <Stack {...textFieldProps} gap={1}>
              <SelectSearchCustomer
                label={tGC('Customer')}
                onChange={handleChangeCustomer}
                disabled={viewMode}
                value={currentCustomer}
                error={Boolean(
                  getFormikField(formik, 'customer.companyName').error,
                )}
                helperText={
                  getFormikField(formik, 'customer.companyName').error
                }
              />
              <Box sx={{ justifyContent: 'space-between', display: 'flex' }}>
                <Link
                  underline="always"
                  sx={{ cursor: 'pointer' }}
                  onClick={openBrowseCustomerModal}
                >
                  {tPS('Browse Customer')}
                </Link>
                <Link
                  underline="always"
                  sx={{ cursor: 'pointer' }}
                  onClick={openCreateCustomerModal}
                >
                  {tGB('Add Customer')}
                </Link>
              </Box>
            </Stack>
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tPS('Customer Reference')}
              name="customerReference"
              value={getFormikField(formik, 'customerReference').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tPS('Tracking Number')}
              name="trackingNumber"
              value={getFormikField(formik, 'trackingNumber').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps}>
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tPS('Ship To')}
              name="shipToName"
              value={getFormikField(formik, 'shipToName').value}
              onChange={formik.handleChange}
            />
          </Grid>
        </Stack>
      </Stack>
    </>
  );
}
